import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import numbro from 'numbro';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils/style';
import { Text } from '../../typography';
import chroma from 'chroma-js';

// FIX: this component does not trigger changes when using register
// it's probably something about the value of the ref not changing manually
// so when you watch the field, it won't trigger the changes

const displayNone = { display: 'none' };

const InputWrapper = styled.div`
  position: relative;
  width: ${(p) => p.theme.dimensions.inputWidth};

  .currency {
    display: inline-flex;
    position: absolute;
    align-items: center;
    top: 0;
    padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};
  }

  .label {
    position: absolute;
    top: 0;
    padding: 0 ${SPACINGS[2]};
    pointer-events: none;
    transform-origin: left;
    transition: transform 0.2s;

    &.up {
      transform: translateY(0) scale(0.8);
    }

    &.down {
      transform: translateY(${SPACINGS[5]}) scale(1);
    }
  }

  .tooltip {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 57px;
  }
`;

type InputStyledProps = {
  error: boolean;
  tooltip: boolean;
  baseColor: string;
  mainColor: string;
  accentColor: string;
  inactiveColor: string;
  dangerColor: string;
  backgroundColor: string;
};
const InputStyled = styled.input<InputStyledProps>`
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  background-clip: padding-box;

  margin: 0;
  margin-bottom: ${(p) => (p.error ? SPACINGS[2] : 0)};
  padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};
  padding-right: ${(p) =>
    p.tooltip ? `calc(57px + ${SPACINGS[2]})` : SPACINGS[2]};

  background-color: ${(p) => p.theme.colors.white};
  width: 100%;
  border: none;
  border-bottom: 1px solid ${(p) => p.baseColor};
  border-radius: 4px 4px 0 0;

  color: ${(p) => p.mainColor};
  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};
  text-align: right;

  &::placeholder {
    color: ${(p) => p.baseColor};
  }

  &:disabled {
    color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:read-only {
    color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:not(:disabled) {
    &:hover,
    &:focus {
      background-color: ${(p) => chroma(p.accentColor).alpha(0.1).hex()};
      outline: none;
      border-bottom: 1px solid ${(p) => p.accentColor};
    }
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

type InputNumberProps = {
  name: string;
  label?: string;
  error?: string | boolean;
  inputRef?:
    | ((instance: HTMLInputElement | null) => void)
    | React.RefObject<HTMLInputElement>;
  style?: React.CSSProperties;
  defaultValue?: number;
  value?: number;
  onChange?: ((v: number | undefined) => void) | undefined;
  onBlur?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  currency?: string;
  placeholder?: string;
  tooltip?: React.ReactNode;
  digits?: number;
  textColor?: string;
  baseColor?: string;
  mainColor?: string;
  accentColor?: string;
  inactiveColor?: string;
  dangerColor?: string;
  backgroundColor?: string;
};

function InputNumber(props: InputNumberProps) {
  const theme = useTheme();

  const {
    name,
    label,
    inputRef,
    error,
    style,
    defaultValue,
    value: controlledValue,
    onChange,
    onBlur,
    autoFocus,
    disabled,
    currency,
    placeholder,
    digits = 2,
    tooltip,
    textColor,
    baseColor = theme.colors.secondary80w,
    mainColor = theme.colors.black,
    accentColor = theme.colors.primary,
    inactiveColor = theme.colors.secondary60w,
    dangerColor = theme.colors.danger,
    backgroundColor = theme.colors.white,
  } = props;

  const { t } = useTranslate();
  const errorStyle = React.useMemo(() => ({ color: theme.colors.danger }), [
    theme,
  ]);

  const numbroOptions = React.useMemo(() => ({ mantissa: digits }), [digits]);

  type InputNumberState = {
    value: number | undefined;
    editValue: string;
    displayValue: string;
    isNumberValid: boolean;
    isFocused: boolean;
  };
  const [state, setState] = React.useReducer<
    React.Reducer<InputNumberState, Partial<InputNumberState>>
  >((s, n) => ({ ...s, ...n }), {
    value: controlledValue ?? defaultValue,
    editValue: '',
    displayValue: '',
    isNumberValid: true,
    isFocused: false,
  });

  const { value, editValue, displayValue, isNumberValid, isFocused } = state;

  React.useEffect(() => {
    if (defaultValue) {
      setState({
        displayValue: numbro(defaultValue).format(numbroOptions),
        editValue: numbro(defaultValue).format(numbroOptions),
      });
    }
  }, [defaultValue, numbroOptions]);

  React.useEffect(() => {
    if (controlledValue) {
      const state: Partial<InputNumberState> = {
        value: controlledValue,
        displayValue: numbro(controlledValue).format(numbroOptions),
      };

      if (!isFocused) {
        state.editValue = numbro(controlledValue).format(numbroOptions);
      }

      setState(state);
    }
  }, [controlledValue, numbroOptions, isFocused]);

  React.useEffect(() => {
    if (autoFocus) {
      setState({
        isFocused: true,
        displayValue: value ? numbro(value).format(numbroOptions) : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const targetValue = e.currentTarget.value;

    const state: Partial<InputNumberState> = {
      editValue: e.currentTarget.value,
    };

    if (targetValue) {
      const number = numbro.unformat(e.currentTarget.value);
      const value = number && digits === 0 ? Math.round(number) : number;
      if (typeof value !== 'undefined') {
        const formatted = numbro(value).format(numbroOptions);
        state.displayValue = formatted;
        state.value = value;
        state.isNumberValid = true;
      } else {
        state.isNumberValid = false;
        state.value = undefined;
        state.displayValue = '';
      }
      if (onChange) {
        onChange(value);
      }
    } else {
      state.value = undefined;
      state.displayValue = '';
      state.isNumberValid = true;
      if (onChange) {
        onChange(undefined);
      }
    }

    setState(state);
  }

  function handleFocus() {
    setState({ isFocused: true });
  }

  function handleBlur() {
    if (onBlur) onBlur();

    const state: Partial<InputNumberState> = {
      isFocused: false,
    };

    if (isNumberValid) {
      state.editValue = displayValue;
    }

    setState(state);
  }

  const inputValue = isFocused
    ? editValue
    : !isNumberValid
    ? t('invalidValue')
    : displayValue;

  const isLabelUp = isFocused || inputValue;

  return (
    <InputWrapper style={style}>
      {label && (
        <Text
          className={`label ${isLabelUp ? 'up' : 'down'}`}
          margin="none"
          type="label"
          htmlFor={name}
        >
          {label}
        </Text>
      )}
      {currency && isLabelUp && (
        <Text className="currency" style={{ color: textColor }} margin="none">
          {currency}
        </Text>
      )}
      <input
        readOnly
        name={name}
        style={displayNone}
        value={value ?? ''}
        ref={inputRef}
      />
      <InputStyled
        id={name}
        name={name}
        error={Boolean(error)}
        value={inputValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        baseColor={baseColor}
        mainColor={textColor ?? mainColor}
        accentColor={accentColor}
        inactiveColor={inactiveColor}
        dangerColor={dangerColor}
        backgroundColor={backgroundColor}
        tooltip={Boolean(tooltip)}
      />
      {tooltip && <div className="tooltip">{tooltip}</div>}
      {typeof error === 'string' && (
        <Text id={`${name}-error`} style={errorStyle}>
          {error}
        </Text>
      )}
    </InputWrapper>
  );
}

export default React.memo(InputNumber);
